import { BubbleMenu, BubbleMenuProps, isNodeSelection } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';

import { cn, legacyTextStyles } from '@eluve/components';

import { SLASH_COMMAND_EXTENSION_NAME } from '../extensions/slash-command';
import { SnippetModal } from '../extensions/snippets';
import {
  getEditorExtensionOptions,
  getEditorSelectedHTML,
} from '../extensions/utils';

import { NodeSelector } from './node-selector';

export interface BubbleMenuItem {
  name: string;
  isActive: () => boolean;
  command: () => void;
  icon?: typeof BoldIcon;
  text?: string;
}

type EditorBubbleMenuProps = Omit<BubbleMenuProps, 'children'>;

export const EditorBubbleMenu: FC<EditorBubbleMenuProps> = (props) => {
  const { editor } = props;

  const [isNodeSelectorOpen, setIsNodeSelectorOpen] = useState(false);
  const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
  const [snippetContent, setSnippetContent] = useState('');
  const [snippetTitle, setSnippetTitle] = useState('');
  const bubbleMenuTippyRef = useRef<any>(null); // Reference to the underline tippy instance
  // const [isColorSelectorOpen, setIsColorSelectorOpen] = useState(false);

  // Control the bubble menu visibility based on the snippet modal. The shouldShow not being called upon opening/closing the modal
  useEffect(() => {
    if (bubbleMenuTippyRef.current && bubbleMenuTippyRef.current.popper) {
      bubbleMenuTippyRef.current.popper.style.display = isSnippetModalOpen
        ? 'none'
        : '';
    }
  }, [isSnippetModalOpen]);

  // do not show BubbleMenu if editor is not editable
  if (!editor?.isEditable) {
    return null;
  }

  const stylingItems: BubbleMenuItem[] = [
    {
      name: 'bold',
      isActive: () => editor?.isActive('bold') ?? false,
      command: () => editor?.chain().focus().toggleBold().run(),
      icon: BoldIcon,
    },
    {
      name: 'italic',
      isActive: () => editor?.isActive('italic') ?? false,
      command: () => editor?.chain().focus().toggleItalic().run(),
      icon: ItalicIcon,
    },
    {
      name: 'underline',
      isActive: () => editor?.isActive('underline') ?? false,
      command: () => editor?.chain().focus().toggleUnderline().run(),
      icon: UnderlineIcon,
    },
    {
      name: 'strike',
      isActive: () => editor?.isActive('strike') ?? false,
      command: () => editor?.chain().focus().toggleStrike().run(),
      icon: StrikethroughIcon,
    },
  ];

  // Only display snippet option if it's enabled in extension
  const slashCommandExtension = getEditorExtensionOptions(
    editor,
    SLASH_COMMAND_EXTENSION_NAME,
  );

  const snippetItems: BubbleMenuItem[] = slashCommandExtension?.snippetOptions
    ?.enabled
    ? [
        {
          name: 'add_snippet',
          isActive: () => false,
          command: () => {
            const selectedHTML = getEditorSelectedHTML(editor!);

            setIsSnippetModalOpen(true);
            setSnippetContent(selectedHTML);
          },
          text: 'Create Snippet',
        },
      ]
    : [];

  const items = [...stylingItems, ...snippetItems];

  const bubbleMenuProps: EditorBubbleMenuProps = {
    ...props,
    shouldShow: ({ state, editor }) => {
      const { selection } = state;
      const { empty } = selection;
      // don't show bubble menu if:
      // - the selection is empty
      // - the selection is a node selection (for drag handles)
      if (empty || isNodeSelection(selection)) {
        return false;
      }
      return true;
    },
    tippyOptions: {
      moveTransition: 'transform 0.15s ease-out',
      onCreate: (instance) => {
        bubbleMenuTippyRef.current = instance;
      },
      onHidden: () => {
        setIsNodeSelectorOpen(false);
        // setIsColorSelectorOpen(false);
      },
    },
  };

  const resetSnippetModal = () => {
    setIsSnippetModalOpen(false);
    setSnippetContent('');
    setSnippetTitle('');
  };

  return (
    // BubbleMenu must be surrounded by div https://github.com/ueberdosis/tiptap/issues/2658
    <>
      <div>
        <BubbleMenu
          {...bubbleMenuProps}
          className="flex w-fit divide-x divide-border-primary rounded border border-border-primary bg-white shadow-xl"
        >
          <NodeSelector
            editor={editor!}
            isOpen={isNodeSelectorOpen}
            setIsOpen={() => {
              setIsNodeSelectorOpen(!isNodeSelectorOpen);
              // setIsColorSelectorOpen(false);
            }}
          />

          <div className="flex">
            {items.map((item, index) => (
              <button
                key={index}
                onClick={item.command}
                className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
                type="button"
              >
                {item.icon ? (
                  <item.icon
                    className={cn('size-4', {
                      'text-bg-accent-strong-primary': item.isActive(),
                    })}
                  />
                ) : (
                  <span
                    className={legacyTextStyles.body({
                      size: 'm',
                      color: 'tertiary',
                    })}
                  >
                    {item.text}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/*  uncomment if we want to enable color

      <ColorSelector
        editor={editor!}
        isOpen={isColorSelectorOpen}
        setIsOpen={() => {
          setIsColorSelectorOpen(!isColorSelectorOpen);
          setIsNodeSelectorOpen(false);
        }}
      /> */}
        </BubbleMenu>
      </div>
      <SnippetModal
        snippet={{ title: snippetTitle, content: snippetContent }}
        isOpen={isSnippetModalOpen}
        onOpenChange={setIsSnippetModalOpen}
        onClose={resetSnippetModal}
        onSave={async (title, content) => {
          await slashCommandExtension?.snippetOptions?.addSnippet?.(
            title,
            content,
          );
          resetSnippetModal();
        }}
      />
    </>
  );
};
